import './experience.scss';
import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import resolveWithEnglishFallback from '../../../../utils/i18n-fallback';
import {
  buildExperienceBlocks,
  findTranslationKey,
  getEventCandidates,
  normalizeExperienceEventNoSpaces,
} from '../../../../utils/experience-keys';
import {
  getExperienceAssetGroupsByPrefix,
  getExperienceAssetsByPrefix,
  tryRequireExperienceAsset,
} from '../../../../utils/experience-assets';
import { getAuthToken, getCurrentRole } from '../../../../utils/auth';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ModalHacShowContact from "../../role-hac/modal-hac-show-contact/modal-hac-show-contact";
import ExperienceTabs from './experience-tabs';
import ExperienceHero from './experience-hero';
import ExperienceInfo from './experience-info';
import ExperienceSlider from './experience-slider';
import ExperienceFooter from './experience-footer';

const EXPERIENCE_ASSET_CONTEXT = require.context(
  '../../../../assets/img/experiences',
  true,
  /\.(png|jpe?g|webp)$/
);

// Example: { costasmeralda: { 4: 'grid' } }
const EXPERIENCE_IMAGE_LAYOUT_BY_EXPERIENCE = {};
const HIDDEN_EXPERIENCE_KEYS = ['privateparis', 'canmulo', 'ibiza'];
const EXPERIENCE_IMAGE_POSITIONS = {
  champagne: { 1: 'end' }
};

const Experience = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { i18n } = useTranslation();
  const translate = (key, options = {}) => resolveWithEnglishFallback(i18n, key, options);

  const token = getAuthToken();
  const role = useMemo(() => getCurrentRole(token), [token]);
  const location = useLocation();
  const navigate = useNavigate();

  const event = useMemo(() => {
    const eventFromState = location.state?.event;
    const eventFromSearch = new URLSearchParams(location.search).get('event');

    return eventFromState || eventFromSearch || "";
  }, [location.search, location.state]);

  const catalogPath = useMemo(() => {
    if (role === 'retailer' || role === 'retailerGM') {
      return '/retailer/main/catalog';
    }

    if (role === 'pma' || role === 'pmaGM') {
      return '/pma/catalog';
    }

    return '/hac/catalog';
  }, [role]);
  const experienceKey = useMemo(() => normalizeExperienceEventNoSpaces(event).toLowerCase(), [event]);
  const isHiddenExperience = useMemo(() => HIDDEN_EXPERIENCE_KEYS.includes(experienceKey), [experienceKey]);
  const eventCandidates = useMemo(() => getEventCandidates(event), [event]);
  const assetEventCandidates = useMemo(() => {
    return experienceKey ? [experienceKey] : [];
  }, [experienceKey]);
  const imageLayoutByCount = useMemo(() => {
    return EXPERIENCE_IMAGE_LAYOUT_BY_EXPERIENCE[experienceKey] || {};
  }, [experienceKey]);
  const imagePosition = EXPERIENCE_IMAGE_POSITIONS[experienceKey];

  const structuredEndingKey = findTranslationKey('experience-ending-structured', eventCandidates, i18n);

  const structuredEnding = structuredEndingKey
    ? translate(structuredEndingKey, { returnObjects: true })
    : null;

  const endingTextKey = findTranslationKey('experience-ending', eventCandidates, i18n);

  const endingText = endingTextKey ? translate(endingTextKey) : '';
  const endingColumn1Key = findTranslationKey('experience-ending-column-1', eventCandidates, i18n);
  const endingColumn2Key = findTranslationKey('experience-ending-column-2', eventCandidates, i18n);
  const endingColumns = [endingColumn1Key, endingColumn2Key]
    .filter(Boolean)
    .map((key) => translate(key));

  const heroTitleKey = findTranslationKey('experience-hero-title', eventCandidates, i18n);
  const heroSubtitleKey = findTranslationKey('experience-hero-subtitle', eventCandidates, i18n);
  const dateKey = findTranslationKey('experience-date', eventCandidates, i18n);
  const introTitleKey = findTranslationKey('experience-intro-title', eventCandidates, i18n);
  const infoIntroKey = findTranslationKey('experience-info-intro', eventCandidates, i18n);

  const heroTitleText = heroTitleKey ? translate(heroTitleKey) : '';
  const heroSubtitleText = heroSubtitleKey ? translate(heroSubtitleKey) : '';
  const dateText = dateKey ? translate(dateKey) : '';
  const introTitleText = introTitleKey ? translate(introTitleKey) : '';
  const infoIntroText = infoIntroKey ? translate(infoIntroKey) : '';


  const [openModal, setOpenModal] = useState(false);

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const showContact = () => {
    setOpenModal(true);
  };

  const hero = tryRequireExperienceAsset(EXPERIENCE_ASSET_CONTEXT, assetEventCandidates, 'hero');
  const blockImages = getExperienceAssetGroupsByPrefix(EXPERIENCE_ASSET_CONTEXT, assetEventCandidates, 'p');
  const blocks = buildExperienceBlocks({
    images: blockImages,
    eventCandidates,
    i18n,
    translate,
    imageLayoutByCount,
  });
  const sliderImages = getExperienceAssetsByPrefix(EXPERIENCE_ASSET_CONTEXT, assetEventCandidates, 'video');

  return (
    <>
      {token && (!event || isHiddenExperience) ? (
        <Navigate to={catalogPath} replace />
      ) : token ? (
        <>
          <ExperienceTabs role={role} navigate={navigate} translate={translate} />
          <ExperienceHero
            experienceKey={experienceKey}
            image={hero}
            title={heroTitleText}
            subtitle={heroSubtitleText}
          />
          <ExperienceInfo
            dateText={dateText}
            introTitleText={introTitleText}
            infoIntroText={infoIntroText}
            blocks={blocks}
            experienceKey={experienceKey}
            structuredEnding={structuredEnding}
            endingText={endingText}
            endingColumns={endingColumns}
            imagePosition={imagePosition}
          />
          <ExperienceSlider experienceKey={experienceKey} images={sliderImages} />
          <ModalHacShowContact
            open={openModal}
            handleClose={handleCloseModal}
            className="modal-contact"
          />
          <ExperienceFooter
            visible={role === 'hac'}
            titleText={translate('experience-footer-title')}
            bodyText={translate('experience-footer-text')}
            contactText={translate('common-contact-assistant')}
            onContact={showContact}
          />

        </>
      ) : (
        <Navigate to="/login"/>
      )}
    </>
  );
};

export default Experience;
